import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { LOCATIONS } from "@/content/locations";
import { RANGPUR_AREAS } from "@/content/rangpur-areas";
import { buildSeo } from "@/lib/seo";
import { SITE } from "@/content/site";
import { ArrowUpRight, MapPin, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export const Route = createFileRoute("/seo-locations")({
  head: () => buildSeo({
    title: "SEO Services by Location, Bangladesh | Freelancer Tamal",
    description: "Geo-targeted SEO across Bangladesh — Rangpur, Dhaka, Chittagong, Sylhet, Saidpur, Dinajpur & Rajshahi. Local & technical SEO built per city.",
    path: "/seo-locations",
    ogEyebrow: "Locations",
    keywords: "SEO services Bangladesh, local SEO Bangladesh, SEO expert by city, Rangpur SEO, Dhaka SEO, Chittagong SEO, Sylhet SEO",
  }),
  component: Locations,
});

function Locations() {
  const [q, setQ] = useState("");
  const [region, setRegion] = useState<string>("all");

  const regions = useMemo(
    () => Array.from(new Set(LOCATIONS.map((l) => l.region))),
    [],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return LOCATIONS.filter((l) => {
      if (region !== "all" && l.region !== region) return false;
      if (!term) return true;
      return (
        l.city.toLowerCase().includes(term) ||
        l.region.toLowerCase().includes(term) ||
        l.district.toLowerCase().includes(term) ||
        l.industries.some((i: string) => i.toLowerCase().includes(term)) ||
        l.neighborhoods.some((n: string) => n.toLowerCase().includes(term))
      );
    });
  }, [q, region]);

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "SEO Services by Location — Bangladesh",
    itemListElement: LOCATIONS.map((l, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE.url}/seo-services/${l.slug}`,
      name: `SEO Expert in ${l.city}`,
    })),
  };

  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "SEO Locations", path: "/seo-locations" }]} />
      <JsonLd data={itemListSchema} />

      {/* HERO + INTRO */}
      <Section headingLevel={1} eyebrow="SEO across Bangladesh" title={<>Local SEO services, <span className="text-gradient">city by city.</span></>}>
        <div className="grid lg:grid-cols-3 gap-10 -mt-2">
          <div className="lg:col-span-2 space-y-5 text-lg text-muted-foreground leading-relaxed">
            <p>
              Bangladesh is not one search market — it's dozens. Buyers in Gulshan look nothing like buyers in Saidpur, and the SERPs reflect that. I build geo-targeted SEO programs that win the actual searches that drive revenue in <strong className="text-foreground">your</strong> city — not generic national keywords.
            </p>
            <p>
              Every city below has its own dedicated landing page covering local industries, neighborhoods, competitive landscape, and the specific SEO playbook I use to help businesses there rank in Google's local pack and organic results. From <strong className="text-foreground">Rangpur</strong> and <strong className="text-foreground">Dhaka</strong> to <strong className="text-foreground">Chittagong</strong>, <strong className="text-foreground">Sylhet</strong>, <strong className="text-foreground">Saidpur</strong>, <strong className="text-foreground">Dinajpur</strong>, and <strong className="text-foreground">Rajshahi</strong> — each market gets a strategy built from the ground up.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {LOCATIONS.map((l) => (
                <a
                  key={l.slug}
                  href={`#${l.slug}`}
                  className="rounded-full border border-border bg-card px-4 py-2 text-sm hover:border-primary hover:text-foreground transition"
                >
                  {l.city}
                </a>
              ))}
            </div>
          </div>
          <div className="rounded-3xl border border-border bg-card p-7">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Quick links</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/seo-services" className="hover:text-primary">→ City × service index</Link></li>
              <li><Link to="/services" className="hover:text-primary">→ All SEO services</Link></li>

              <li><Link to="/case-studies" className="hover:text-primary">→ Case studies</Link></li>
              <li><Link to="/free-seo-audit" className="hover:text-primary">→ Free SEO audit tool</Link></li>
              <li><Link to="/contact" className="hover:text-primary">→ Start a project</Link></li>
            </ul>
          </div>
        </div>
      </Section>

      {/* SEARCH + FILTER + GRID */}
      <Section eyebrow="Find your city" title={<>Pick your <span className="text-gradient">market.</span></>}>
        <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between mb-8">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by city, district, industry, neighborhood…"
              className="w-full rounded-full border border-border bg-card pl-11 pr-4 py-3 text-sm outline-none focus:border-primary"
              aria-label="Search locations"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setRegion("all")}
              className={`rounded-full border px-4 py-2 text-sm transition ${region === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
            >
              All regions
            </button>
            {regions.map((r) => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                className={`rounded-full border px-4 py-2 text-sm transition ${region === r ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-3xl border border-border bg-card p-10 text-center text-muted-foreground">
            No cities match "{q}". Try a different keyword or reset the filter.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((l, i) => (
            <Reveal key={l.slug} delay={i * 0.04}>
              <Link
                id={l.slug}
                to="/seo-services/$location"
                params={{ location: l.slug }}
                className="group block rounded-3xl border border-border p-7 bg-card h-full hover:border-primary transition scroll-mt-32"
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  <MapPin size={12} /> {l.region}
                </div>
                <h3 className="mt-3 font-display text-2xl">SEO Expert in {l.city}</h3>
                <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{l.intro}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {l.industries.slice(0, 3).map((ind: string) => (
                    <span key={ind} className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground">{ind}</span>
                  ))}
                </div>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Explore {l.city} SEO <ArrowUpRight size={14} />
                </span>
              </Link>
            </Reveal>
            ))}
          </div>
        )}
      </Section>

      <Section className="!pt-0">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/15 via-card to-card p-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl">Don't see your city?</h2>
          <p className="mt-3 text-muted-foreground">I work with clients across Bangladesh and globally. Tell me about your market.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium">
              Contact me <ArrowUpRight size={16} />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-card">
              See services
            </Link>
          </div>
        </div>
      </Section>

      <Section eyebrow="Hyperlocal · Rangpur" title={<>Every <span className="text-gradient">Rangpur neighborhood.</span></>}>
        <p className="max-w-2xl text-muted-foreground -mt-6 mb-8">
          Dedicated landing pages for the bazaars, intersections, campuses, and corridors buyers search by name —
          from Medical Mor and Shapla Chattar to Begum Rokeya University, Tajhat, and Haragash.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {RANGPUR_AREAS.map((a) => (
            <Link
              key={a.slug}
              to="/seo-services/rangpur/$area"
              params={{ area: a.slug }}
              className="group rounded-2xl border border-border bg-card px-4 py-3 hover:border-primary transition flex items-center justify-between gap-2"
            >
              <span className="text-sm">SEO near <span className="font-medium text-foreground">{a.name}</span></span>
              <ArrowUpRight size={14} className="text-muted-foreground group-hover:text-primary" />
            </Link>
          ))}
        </div>
        <div className="mt-6">
          <Link to="/seo-services/$location" params={{ location: "rangpur" }} className="text-sm text-primary hover:underline">
            → Rangpur SEO hub
          </Link>
        </div>
      </Section>
    </>
  );
}
